<template>
  <div class="fill">
    <section class="section data-cards-section">
      <div class="container is-fluid">
        <h1 class="title">Valle Camonica Covid-19</h1>
        <h2 class="subtitle">
          Dati aggiornati al {{ lastUpdated }}
        </h2>

        <!-- Desktop cards -->
        <div class="columns is-mobile is-multiline">
          <div class="column is-3-desktop is-6-mobile">
            <DataCard
              title="Casi"
              :body="this.cardString(stats.cases)"
              bodyClasses="has-text-danger" />
          </div>

          <div class="column is-3-desktop is-6-mobile">
            <DataCard
              title="Guariti"
              :body="this.cardString(stats.recovered)"
              bodyClasses="has-text-success" />
          </div>

          <div class="column is-3-desktop is-6-mobile">
            <DataCard
              title="Dimessi al Domicilio"
              :body="this.cardString(stats.home)"
              bodyClasses="has-text-success" />
          </div>

          <div class="column is-3-desktop is-6-mobile">
            <DataCard
              title="Deceduti"
              :body="this.cardString(stats.deceased)"
              bodyClasses="has-text-danger" />
          </div>
        </div>
      </div>
    </section>

    <section class="section map-section">
      <div class="container is-fluid">
        <div class="columns is-mobile">
          <div class="column map-col">
            <LMap
                ref="casesMap"
                :zoom="zoom"
                :center="center">
                  <LTileLayer
                  ref="geojsonLayer"
                  :url="url"
                  :attribution="attribution" />
              </Lmap>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script>

import L from 'leaflet';
import { latLng } from "leaflet";
import { LMap, LTileLayer } from 'vue2-leaflet';
import DataCard from "@/components/DataCard.vue";

import meta from '@/assets/data/meta.json'
import councils from '@/assets/data/comuni.json'

export default {
  name: 'Map',
  components: {
    LMap,
    LTileLayer,
    DataCard
  },
  data: function () {
    return {
      zoom: 10,
      center: latLng(46.051452, 10.348635),
      url: 'https://cartodb-basemaps-{s}.global.ssl.fastly.net/rastertiles/voyager/{z}/{x}/{y}.png',
      attribution:
        '&copy; <a href="http://www.openstreetmap.org/copyright">OpenStreetMap</a>, &copy; <a href="https://carto.com/attribution">CARTO</a>',
      geojson: councils,
      geojsonLayer: null,
      shapesOpacity: 0.7,
      infoBox: null,
      lastUpdated: meta.lastUpdate,
      highlightedLayer: null,
      stats: meta.stats
    }
  },
  mounted () {
    this.$nextTick(() => {
      var legend = L.control({position: 'bottomright'});

      legend.onAdd = this.onAddLegend;
      legend.addTo(this.$refs.casesMap.mapObject);

      this.geojsonLayer = L.geoJson(
        this.geojson, {
          style: this.styleFunction,
          onEachFeature: this.onEachFeatureFunction
        }).addTo(this.$refs.casesMap.mapObject);

      // Create the info box
      this.infoBox = L.control();

      this.infoBox.onAdd = function () {
          this._div = L.DomUtil.create('div');
          this.update();
          return this._div;
      };

      this.infoBox.update = function (props) {
        let infoText = '';

        if (props) {
          let casesDelta = props.cases - props.cases_yesterday;

          if (casesDelta >= 0) {
            casesDelta = `+${casesDelta}`
          }

          infoText = `
            <b class="is-size-5">${props.name}</b>
            <br />
            <p class="is-size-6">${props.cases} casi (${casesDelta})</p>`
        } else {
          infoText = "<p class='is-size-6'>Seleziona un comune</p>"
        }

        this._div.innerHTML = `
          <div class="data-card">
            ${infoText}
            <div class="credits-link">
              <a class="is-size-7" href="/credits">Riconoscimenti</a>
            </div>
          </div>
          `
      };

      this.infoBox.addTo(this.$refs.casesMap.mapObject);
    });
  },
  computed: {
    styleFunction() {
      return (feature) => {
        let cases = feature.properties.cases;
        
        return {
          weight: 2,
          opacity: 1,
          color: "#FFF",
          dashArray: '3',
          fillColor: this.getColor(cases),
          fillOpacity: this.shapesOpacity
        };
      };
    },
    onEachFeatureFunction() {
      return (feature, layer) => {
        layer.on({
          click: this.selectCouncil
        });
      };
    }
  },
  methods: {
    cardString(stats) {
      let delta = stats.today - stats.prev;

      if (delta >= 0) {
        return stats.today + " (+" + delta + ")";
      }

      return stats.today + " (" + delta + ")";
    },
    selectCouncil (e) {
      if (this.highlightedLayer) {
        this.geojsonLayer.resetStyle(this.highlightedLayer);
        this.infoBox.update();
      }

      var layer = e.target;

      layer.setStyle({
          weight: 4,
          color: '#2B2B2B',
          dashArray: '',
          fillOpacity: this.shapesOpacity
      });

      if (!L.Browser.ie && !L.Browser.opera && !L.Browser.edge) {
          layer.bringToFront();
      }

      this.highlightedLayer = layer;
      this.infoBox.update(layer.feature.properties);
    },
    getColor (cases) {
      // https://material.io/design/color/
      return cases > 75  ? '#0D47A1' :
             cases > 50  ? '#1E88E5' :
             cases > 25  ? '#42A5F5' : '#90CAF9';
    },
    onAddLegend () {
      var div = L.DomUtil.create('div', 'info data-card legend-card');
      let cases = [0, 25, 50, 75];
      let labels = ["0-25", "25-50", "50-75", "75+"]

      // loop through our density intervals and generate a label with a colored square for each interval
      let innerHTML = "";

      for (var i = 0; i < cases.length; i++) {
          let rowColor = this.getColor(cases[i] + 1);
 
          innerHTML += `
            <div class="column is-one-quarter">
              <div class="columns is-mobile">
                <div class="column is-3 legend-color-col">
                  <div class="legend-color" style="background-color: ${rowColor};"></div>
                </div>
                <div class="column legend-text is-size-6">${labels[i]}</div>
              </div>
            </div>
          `
      }

      div.innerHTML = `
        <div class="columns is-vcentered is-mobile">
          ${innerHTML}
        </div>
      `

      return div;
    }
  }
}

</script>

<style scoped>

.fill {
  position: absolute;
  top: 0px;
  bottom: 0px;
  left: 0px;
  right: 0px;
}

.data-cards-section {
  padding-bottom: 0px;
}

.map-col {
  height: 80vh;
}

@media only screen and (max-width: 768px) {
  .map-section {
    padding: 1.5rem 1rem;
  }

  .map-col {
    height: 90vh;
  }
}

.info-box {
  position: absolute;
  top: 16px;
  right: 16px;
  background-color: white;
  padding: 16px;
}

</style>

<style>

.data-card {
  background-color: white;
  padding: 16px;
  box-shadow: 2px 2px 10px rgba(0, 0, 0, 0.34);
}

.legend-color {
  width: 24px;
  height: 24px;
}

.legend-text {
  text-align: right;
  padding-left: 0px !important;
}

.last-update-text {
  margin-bottom: 8px;
}

.legend-card {
  margin-left: 10px;
}

@media only screen and (max-width: 768px) {
  .legend-text {
    padding-right: 8px !important;
  }

  .legend-color-col {
    padding-left: 4px !important;
  }

  .data-card {
    padding: 8px;
  }

  .legend-card {
    padding-left: 16px;
    padding-right: 16px;
  }
}

.credits-link {
  padding-top: 8px !important;
}

</style>